// Download made in this browser (spec E2, E2.1b and E2.2): the engine's export runs here with the
// chosen preset's or Custom's settings, its progress feeds live's bell and the line under the
// header, and the file is saved under the bell's name, `Download_YYYYMMDD_HHMM.<format>`.
import { useCallback, useEffect, useRef, useState } from "react";

import { DownloadError, fetchSignedFile, saveBlob } from "../../api/download";
import { extensionOf, type ExportSettings } from "./engine/presets";
import type { EngineProject } from "./engine/project";
import { frameCount } from "./engine/schedule";
import { removeExportFiles, removeStaleExportFiles } from "./engine/opfs";
import { engineSupported } from "./engine/support";
import { bellName, type LocalExport } from "./exports";
import { newId } from "./timeline";

/** An export's file in the browser's own file system: its id, with its format. */
function fileOf(id: string, extension: string): string {
  return `${id}.${extension}`;
}

/** How often, at most, the bar moves: the export runs at its own pace. */
const PROGRESS_STEP_MS = 100;

/** Why an export in the browser stopped, in words. */
function problemOf(error: unknown): string {
  if (error instanceof DownloadError) {
    if (error.status === 403 || error.status === 404 || error.status === 410) {
      return "A clip's file link had expired; try again.";
    }
    return error.status === undefined
      ? "A clip's file could not be fetched here."
      : `A clip's file could not be fetched (status ${error.status}).`;
  }
  return error instanceof Error && error.name === "SourceError"
    ? error.message
    : "This browser could not make the video; try again.";
}

/** What `start` did: the export runs, or the browser cannot make it, and why. */
export type StartResult = { kind: "started" } | { kind: "refused"; reason: string };

/**
 * The editor's exports made in the browser, newest first. `start` refuses at once, saying why,
 * when this browser cannot make the file (no WebGL, no WebCodecs, or no encoder for a codec the
 * settings name); the caller may then ask the server. An export still running when the editor
 * closes is stopped.
 */
export function useLocalExports() {
  const [exports, setExports] = useState<readonly LocalExport[]>([]);
  const running = useRef(new Map<string, AbortController>());
  useEffect(() => {
    // The files earlier visits left in the browser's file system.
    void removeStaleExportFiles();
    const controllers = running.current;
    return () => {
      for (const controller of controllers.values()) controller.abort();
    };
  }, []);
  const patch = useCallback((id: string, change: Partial<LocalExport>) => {
    setExports((current) => current.map((each) => (each.id === id ? { ...each, ...change } : each)));
  }, []);

  const start = useCallback(
    async (project: EngineProject, settings: ExportSettings): Promise<StartResult> => {
      const nothing = "This browser cannot make the file: it needs WebCodecs (Chrome or Edge 94+, Opera 80+).";
      // Not even fetched where it cannot run (no WebGL for a video, no WebCodecs at all).
      if (typeof AudioEncoder === "undefined" && typeof VideoEncoder === "undefined") {
        return { kind: "refused", reason: nothing };
      }
      if (settings.video !== null && !engineSupported()) return { kind: "refused", reason: nothing };
      // The engine and mediabunny load only now, with the first export.
      const engine = await import("./engine/export");
      const reason = await engine.refusalOf(project, settings);
      if (reason !== null) return { kind: "refused", reason };
      const id = newId();
      const startedAt = Date.now();
      const extension = extensionOf(settings);
      const frames = settings.video === null ? 1 : frameCount(project);
      setExports((current) => [
        {
          id,
          startedAt,
          done: 0,
          frames,
          state: "exporting",
          problem: null,
          file: null,
          extension,
        },
        ...current,
      ]);
      const controller = new AbortController();
      running.current.set(id, controller);
      let shown = 0;
      const run = async () => {
        try {
          const file = await engine.exportFile(project, settings, {
            fetchFile: fetchSignedFile,
            signal: controller.signal,
            onProgress: (done, all) => {
              const now = performance.now();
              if (now - shown < PROGRESS_STEP_MS && done < all) return;
              shown = now;
              patch(id, { done, frames: all });
            },
            // Its own file in the browser's file system while the bell lists it (decision 28).
            name: fileOf(id, extension),
          });
          saveBlob(file, bellName(startedAt, extension));
          patch(id, { state: "saved", file });
        } catch (error) {
          if (!controller.signal.aborted) patch(id, { state: "failed", problem: problemOf(error) });
        } finally {
          running.current.delete(id);
        }
      };
      void run();
      return { kind: "started" };
    },
    [patch],
  );

  /** Saves a made file again under its name, as the bell's "Download again". */
  const saveAgain = useCallback((file: Blob, name: string) => saveBlob(file, name), []);
  /** Drops the finished ones from the bell, and their files from the browser's file system. */
  const clear = useCallback(() => {
    setExports((current) => {
      const done = current.filter((each) => each.state !== "exporting");
      void removeExportFiles(done.map((each) => fileOf(each.id, each.extension)));
      return current.filter((each) => each.state === "exporting");
    });
  }, []);
  return { exports, start, saveAgain, clear };
}
